import { Stripe, loadStripe } from '@stripe/stripe-js';

// Stripe.js is fetched from js.stripe.com, which ad-blockers and flaky
// networks regularly block (TA-10957 / FRONTEND-2YT). After a failure
// `loadStripe` clears its cached promise and, on the next call, removes the
// dead <script> and injects a fresh one — so retrying really re-fetches it
// (locked down in loadStripeWithRetry.script.fast.test.ts).
export const STRIPE_LOAD_RETRY_DELAY_MS = 1000;
// An offline request can stay pending forever without firing load/error, so
// each attempt is capped; Stripe.js normally loads in well under a second.
export const STRIPE_LOAD_TIMEOUT_MS = 8000;

const STRIPE_SCRIPT_SELECTOR = 'script[src^="https://js.stripe.com/v3"]';

/**
 * Fails a Stripe.js <script> that never settled. `loadStripe` caches its
 * pending promise and only clears it on the script's `error` event, so a
 * timed-out load would otherwise be replayed by every retry. Dispatching
 * `error` rejects and clears that cache; removing the node makes the next
 * `loadStripe` call inject a fresh script.
 */
export const abortPendingStripeScript = () => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(STRIPE_SCRIPT_SELECTOR).forEach((script) => {
    script.dispatchEvent(new Event('error'));
    script.remove();
  });
};

type Options = {
  retries?: number;
  delayMs?: number;
  timeoutMs?: number;
  loader?: (publishableKey: string) => Promise<Stripe | null>;
  onTimeout?: () => void;
};

const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  onTimeout: () => void
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout();
      reject(new Error(`Stripe.js load timed out after ${timeoutMs}ms`));
    }, timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

export const loadStripeWithRetry = async (
  publishableKey: string,
  {
    retries = 1,
    delayMs = STRIPE_LOAD_RETRY_DELAY_MS,
    timeoutMs = STRIPE_LOAD_TIMEOUT_MS,
    loader = loadStripe,
    onTimeout = abortPendingStripeScript,
  }: Options = {}
): Promise<Stripe> => {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      // Always yield at least one macrotask so loadStripe's own cache-clearing
      // `.catch` handlers run before the next attempt
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    try {
      const stripe = await withTimeout(
        loader(publishableKey),
        timeoutMs,
        onTimeout
      );
      // loadStripe resolves null in non-browser environments; treat that as a
      // failure too so the caller shows the fallback instead of a dead form.
      if (stripe) return stripe;
      lastError = new Error('Stripe.js not available');
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
};
